import useNavigationStore from '@src/stores/navigationStore';
import useRegistrationStore from '@src/stores/registrationStore';
import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import BackButton from '@src/components/_shared/BackButton';
import Container from '@src/components/_shared/Container';
import { getReferencialData } from '@src/api/Referencial';
import { useMutation, useQuery } from '@tanstack/react-query';
import CountryDropdown from '@src/components/_shared/UI/CountryDropdownList';
import { Form, Formik } from 'formik';
import AddressSearch from '@src/components/_shared/UI/AddressSearch';
import { useEffect, useState } from 'react';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import Input from '@src/components/_shared/UI/Input';
import { AddressEnumType, AddressType, SearchAddressType } from '@src/utilities/types';
import { personAdress, updatePersonAddresses } from '@src/api/Registration';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import { handleApiError } from '@src/utilities/functions';
import { addressesSchema } from '@src/utilities/schemas';

/**
    @todo: Ajouter schemas de validaton sur champs
**/
export default function HomeAddress() {
    const { setSection, setBlock } = useNavigationStore();
    const { setField, registration } = useRegistrationStore();
    const [homeAddress, setHomeAddress] = useState<any>({...registration.addresses.find(address => address.address_type === AddressEnumType.postale)} as AddressType);
    const [notFound, setNotFound] = useState<boolean>(false);
    const [submitted, setSubmitted] = useState<boolean>(false);

    const _onGoBack = () => {
        setSection(2)
        setBlock('FinancialJob')
    };

    const { data } = useQuery({
        queryKey: [`getCountriesData`],
        queryFn: () => getReferencialData({type: 'countries'})
    })

    const { mutate, isPending } = useMutation({
        mutationFn: () => personAdress({ addressValue: homeAddress, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id }), 
        onSuccess: (data: any) => {
            registration.addresses.filter((obj) => {
                return obj.address_type === data.type ? obj.id = data.id : null;
            });
            setBlock('FiscalAddress');
        },
        onError: (error: any) => handleApiError(error)
    });

    const { mutate: MutateUpdateAddresses, isPending: isPendingUpdateAddresses} = useMutation({
        mutationFn: () => updatePersonAddresses({ addressesValue: homeAddress, id: registration.addresses[0].id, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id }),
        onSuccess: () => {
            setBlock('FiscalAddress');
        },
        onError: (error: any) => handleApiError(error)
    });

    const handleSelectCountry = (value: string) => {
        setField('person.basic.residence_country', value);
    };

    const _onSelectExistingAddress = (searchAddress: SearchAddressType) => {
        const regex = /^([0-9]+)\s*(b|bis|t|ter|q|quater)?$/i;
        const result = (searchAddress.housenumber || '').trim().match(regex);
        const number = result && result[1] ? result[1] : '';
        const extension = result && result[2] ? result[2] : '';
        setHomeAddress((homeAddress: AddressType) => ({
            ...homeAddress,
            street_number: number || searchAddress.housenumber,
            street_extension: extension.charAt(0).toUpperCase() || '',
            street_name: searchAddress.street,
            city: {
                ...homeAddress.city,
                zipcode: searchAddress.postcode,
                name: searchAddress.city,
            },
            country: registration.person.basic.residence_country,
        }));
    }

    const _onValidateHomeAddress = () => {
        registration.addresses.filter(obj => obj.address_type === AddressEnumType.postale).forEach((obj) => {
            obj.street_number = homeAddress.street_number;
            obj.street_extension = homeAddress.street_extension;
            obj.street_name_further = homeAddress.street_name_further;
            obj.street_name = homeAddress.street_name;
            obj.city.zipcode = homeAddress.city.zipcode;
            obj.city.name = homeAddress.city.name;
            obj.country = homeAddress.country;
        });
        if (homeAddress.id) {
            MutateUpdateAddresses();
        } else {
            mutate();
        }
    }

    useEffect(() => {
        if (submitted) {
            _onValidateHomeAddress();
        }
        return () => {
            setSubmitted(false);
        }
    }, [submitted, homeAddress]);

    return (
        <Container>
            <BackButton onClick={_onGoBack}/>
            <BlockTitle title="Adresse domicile du représentant légal" />
            <Subtitle content="Les conditions d’usage et de service dépendent de votre pays de résidence" />
            {data ? 
                <CountryDropdown
                    title="Sélectionnez un pays"
                    width="100%"
                    options={data}
                    selected={registration.person.basic.residence_country}
                    onSelect={handleSelectCountry}
                />
                : null }
            {registration.person.basic.residence_country && (
                <>
                    {!notFound && registration.person.basic.residence_country === 'FR' && (
                        <>
                            <AddressSearch 
                                {...{
                                    selectedAddress: homeAddress,
                                    setAddress: setHomeAddress, 
                                    _onSelectExistingAddress, 
                                    setNotFound
                                }} 
                            />
                            <ValidateButton 
                                title="Suivant" 
                                disabled={!homeAddress.city.zipcode} 
                                onClick={_onValidateHomeAddress} 
                                onLoading={isPending || isPendingUpdateAddresses} 
                            />
                        </>
                    )}
                </>
            )}
            {(notFound || (registration.person.basic.residence_country && registration.person.basic.residence_country !== 'FR')) && 
                <Formik
                    initialValues={{
                        street_number: homeAddress.street_number + ' ' + homeAddress.street_extension,
                        street_name_further: homeAddress.street_name_further,
                        street_name: homeAddress.street_name,
                        city: {
                            name: homeAddress.city.name,
                            zipcode: homeAddress.city.zipcode,
                        }
                    }}
                    onSubmit={values => {
                        const regex = /^([0-9]+)\s*(b|bis|t|ter|q|quater)?$/i;
                        const result = (values.street_number || '').trim().match(regex);
                        const number = result && result[1] ? result[1] : '';
                        const extension =  result && result[2] ? result[2] : '';
                        setHomeAddress((homeAddress: AddressType) => ({
                            ...homeAddress,
                            street_number: number,
                            street_extension: extension.charAt(0).toUpperCase(),
                            street_name: values.street_name,
                            street_name_further: values.street_name_further,
                            city: values.city,
                            country: registration.person.basic.residence_country
                        }))
                        setSubmitted(true);
                    }}
                    validationSchema={addressesSchema}
                >
                    {({ handleChange, handleSubmit, values, errors, isValid, touched, handleBlur }) => {
                        return(
                            <Form id="homeAddressForm" autoComplete="off" className="registerForm">
                                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%'}}>
                                    <div style={{width: '16%'}}>
                                        <Input
                                            title="N°"
                                            value={values.street_number}
                                            onChange={handleChange('street_number')}
                                            id="street_number"
                                            name="street_number"
                                            type="text"
                                            style={{ paddingRight: 16, width: '100%', textAlign: 'center' }}
                                            hasError={(touched.street_number && errors.street_number) as boolean}
                                            onBlur={handleBlur('street_number')}
                                        />
                                    </div>
                                    <div style={{width: '81%'}}>
                                        <Input
                                            title="Rue / voie"
                                            value={values.street_name}
                                            onChange={handleChange('street_name')}
                                            placeholder="Ex : rue de la colombe"
                                            id="street_name"
                                            name="street_name"
                                            type="text"
                                            width="100%"
                                            clearInput={() => handleChange('street_name')('')}
                                            hasError={(touched.street_name && errors.street_name) as boolean}
                                            errorMessage={errors.street_name as string}
                                            onBlur={handleBlur('street_name')}
                                        />
                                    </div>
                                </div>
                                <Input
                                    title="Complément d’adresse (optionnel)"
                                    value={values.street_name_further}
                                    onChange={handleChange('street_name_further')}
                                    placeholder=""
                                    id="street_name_further"
                                    name="street_name_further"
                                    type="text"
                                    width="100%"
                                    clearInput={() => handleChange('street_name_further')('')}
                                    onBlur={handleBlur('street_name_further')}
                                />
                                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                                    <div style={{width: '30%'}}>
                                        <Input
                                            title="Code postal"
                                            value={values.city.zipcode}
                                            onChange={handleChange('city.zipcode')}
                                            placeholder="Ex : 75001"
                                            id="zipcode"
                                            name="zipcode"
                                            type="text"
                                            width="100%"
                                            hasError={(touched.city?.zipcode && errors.city?.zipcode) as boolean}
                                            onBlur={handleBlur('city.zipcode')}
                                        />
                                    </div>
                                    <div style={{width: '67%'}}>
                                        <Input
                                            title="Ville"
                                            value={values.city.name}
                                            onChange={handleChange('city.name')}
                                            placeholder="Ex : Paris"
                                            id="cityname"
                                            name="cityname"
                                            type="text"
                                            width="100%"
                                            clearInput={() => handleChange('city.name')('')}
                                            hasError={(touched.city?.name && errors.city?.name) as boolean}
                                            errorMessage={errors.city?.name as string}
                                            onBlur={handleBlur('city.name')}
                                        />
                                    </div>
                                </div>
                                <ValidateButton title="Continuer" onClick={handleSubmit} disabled={!isValid} onLoading={isPending || isPendingUpdateAddresses} />
                            </Form>
                        )
                    }}
                </Formik>
            }
        </Container>
    );
}
